import styled from '@emotion/styled';
import { Layout } from 'antd';
import { observer } from 'mobx-react-lite';
import { Suspense, type ComponentProps, type ReactNode } from 'react';
import { Outlet } from 'react-router';

import { CoBrandingLogo, CoBrandingLogoVariant } from '../../../assets/Logo/CoBranding/CoBrandingLogo';
import { LogoDestination, LogoSizes } from '../../../assets/Logo/Logo';
import { createApplicationsMenuItems } from '../../../components/apps-menu/createApplicationsMenuItems';
import { SideContactUsButton } from '../../../components/SubmitFeedback/SideContactUsButton';
import { getOrCreateFeatureFlag } from '../../../config/FeatureFlag';
import { UserMenuPopup } from '../../../pages/common/UserMenu/UserMenuPopup';
import { useFeatureFlag } from '../../../pages/summary/behaviours/useFeatureFlag';
import { useAuthStore } from '../../../stores/authStore';
import { useNavinaData } from '../../../utils/useNavinaData';
import { HeaderLogos } from './HeaderLogos';
import { MainLayoutApplicationsMenu } from './MainLayoutApplicationsMenu/MainLayoutApplicationsMenu';
import { MainLayoutHeader, type LayoutHeaderProps } from './MainLayoutHeader';

export { type LayoutHeaderProps } from './MainLayoutHeader';

const StyledLayout = styled(Layout)`
	block-size: 100%;
`;

const LayoutContent = styled(Layout.Content)`
	flex-grow: 1;
	flex-shrink: 1;
	flex-basis: auto;
	overflow-y: auto;
`;

const EmptyDiv = styled.div``;

interface MainLayoutProps extends LayoutHeaderProps {
	readonly headerAppMenuSelectedPage: NonNullable<LayoutHeaderProps['headerAppMenuSelectedPage']>;
	readonly layoutContentProps?: ComponentProps<typeof Layout.Content>;
}

export const MainLayout = observer(function MainLayout({
	layoutContentProps,
	headerLogoLink,
	headerAppMenuSelectedPage,
	applicationsMenu: overrideApplicationsMenu,
	...headerProps
}: MainLayoutProps) {
	const { showFeedbackButton, coBrandingLogoSetup } = getOrCreateFeatureFlag();
	const { isOverlayOrEmbedded } = useNavinaData();
	const authStore = useAuthStore();

	const isOrganizationQuality = useFeatureFlag('configuration_OrganizationQuality', false);
	const isAdminToolEnabled = useFeatureFlag('flag_AdminTool', false);

	const applicationsMenuItems = createApplicationsMenuItems({
		authStore,
		selectedPage: headerAppMenuSelectedPage,
		isOrganizationQuality,
		isAdminToolEnabled,
	});

	const applicationsMenu: ReactNode =
		overrideApplicationsMenu === undefined ? (
			<MainLayoutApplicationsMenu {...{ items: applicationsMenuItems, title: 'Navina Applications' }} />
		) : (
			overrideApplicationsMenu
		);

	const coBrandingLogo = coBrandingLogoSetup.whiteLogoPath ? (
		<CoBrandingLogo
			{...{
				setup: coBrandingLogoSetup,
				variant: CoBrandingLogoVariant.White,
				size: LogoSizes.Base,
				destination: headerLogoLink ?? LogoDestination.Schedule,
				disableForwardToPage: isOverlayOrEmbedded,
			}}
		/>
	) : null;

	return (
		<StyledLayout>
			<MainLayoutHeader
				{...{
					...headerProps,
					logo: <HeaderLogos {...{ headerLogoLink, coBrandingLogo, disableForwardToPage: isOverlayOrEmbedded }} />,
					applicationsMenu,
					userPreferences: <UserMenuPopup />,
				}}
			/>
			<LayoutContent {...layoutContentProps}>
				<Suspense fallback={<EmptyDiv />}>
					<Outlet />
				</Suspense>
			</LayoutContent>
			{showFeedbackButton && <SideContactUsButton />}
		</StyledLayout>
	);
});
